// Static searchable index for the ⌘K command palette.
// Covers pages that live in the frontend router only (pillars,
// glossary, static marketing pages, admin quick jumps) and therefore
// aren't in the backend `/api/search` index.
//
// The CommandBar merges these with the API results so a search for
// "kimméridgien" surfaces both the glossary entry AND any blog post,
// and typing "cartographie" jumps straight to the pillar page.
import {
  Book, Map, Compass, Landmark, Layers, ShoppingBag, GraduationCap,
  FileText, Sparkles, MessageCircle, Info, Newspaper, Grape, MapPin,
  Wine, Building2, Home, Mail,
} from "lucide-react";
import { GLOSSARY, CATEGORIES } from "@/data/glossaire";

// Static page entries (routed in App.js, no backend record).
export const STATIC_PAGES = [
  { kind: "page", slug: "home", url: "/", title: "Accueil", excerpt: "Cartes des vins, formations, application interactive", Icon: Home, tags: ["accueil", "home", "vinum"] },
  { kind: "page", slug: "shop", url: "/shop", title: "Boutique cartes", excerpt: "Toutes nos cartes des vignobles imprimées", Icon: ShoppingBag, tags: ["boutique", "shop", "cartes", "poster"] },
  { kind: "page", slug: "academy", url: "/academy", title: "Academy", excerpt: "Parcours pédagogiques du vin (WSET Level 1 à 4)", Icon: GraduationCap, tags: ["academy", "formation", "wset", "cours"] },
  { kind: "page", slug: "app", url: "/app", title: "Application interactive", excerpt: "Explore le monde du vin par carte, région, cépage, producteur", Icon: MapPin, tags: ["app", "application", "carte interactive"] },
  { kind: "page", slug: "blog", url: "/blog", title: "Blog & Actualités", excerpt: "Articles, dossiers, actualités viticoles", Icon: Newspaper, tags: ["blog", "actualités", "article"] },
  { kind: "page", slug: "about", url: "/about", title: "À propos", excerpt: "L'histoire et la mission de geoVINUM", Icon: Info, tags: ["about", "à propos", "audrey"] },
  { kind: "page", slug: "faq", url: "/faq", title: "FAQ", excerpt: "Questions fréquentes sur nos cartes et l'application", Icon: MessageCircle, tags: ["faq", "questions", "aide"] },
  { kind: "page", slug: "contact", url: "/contact", title: "Contact", excerpt: "Écrire à Audrey et à l'équipe geoVINUM", Icon: Mail, tags: ["contact", "email", "audrey"] },
  { kind: "page", slug: "pro", url: "/pro", title: "Pour les pros", excerpt: "Offres B2B pour cavistes, restaurateurs, écoles", Icon: Building2, tags: ["pro", "b2b", "cavistes", "restaurateurs"] },
  { kind: "page", slug: "products-iframe", url: "/products/iframe", title: "Application iframe interactive", excerpt: "Intégrer notre carte des vins sur votre site", Icon: MapPin, tags: ["iframe", "embed", "intégration"] },

  // GEO/SEO pillar pages
  { kind: "pillar", slug: "glossaire", url: "/glossaire", title: "Glossaire du vin (506 termes)", excerpt: "Dictionnaire complet du vocabulaire viticole, dégustation, terroir", Icon: Book, tags: ["glossaire", "définitions", "vocabulaire", "wset"] },
  { kind: "pillar", slug: "cartographie-viticole", url: "/cartographie-viticole", title: "Cartographie viticole", excerpt: "L'art de représenter les vignobles sur une carte : histoire, méthodes, SIG", Icon: Map, tags: ["cartographie", "sig", "cartes"] },
  { kind: "pillar", slug: "geographie-du-vin", url: "/geographie-du-vin", title: "Géographie du vin", excerpt: "Régions viticoles du monde, climats, latitudes, altitude", Icon: Compass, tags: ["géographie", "régions", "climats", "monde"] },
  { kind: "pillar", slug: "appellations-viticoles", url: "/appellations-viticoles", title: "Appellations viticoles", excerpt: "AOC, AOP, DOP, AVA, hiérarchie, cahiers des charges", Icon: Landmark, tags: ["appellation", "aoc", "aop", "ava", "doc", "docg", "cru"] },
  { kind: "pillar", slug: "terroirs-viticoles", url: "/terroirs-viticoles", title: "Terroirs viticoles", excerpt: "Sols, sous-sols, climat, exposition — la science du terroir", Icon: Layers, tags: ["terroir", "sol", "sous-sol", "climat", "kimméridgien"] },
];

// Turn every glossary entry into a searchable index item pointing to
// its anchor on /glossaire. Category is included in searchable tags so
// "cépage" matches all cépage entries at once.
export const GLOSSARY_INDEX = GLOSSARY.map((t) => {
  const cat = CATEGORIES.find((c) => c.key === t.category);
  return {
    kind: "term",
    slug: t.slug,
    url: `/glossaire#${t.slug}`,
    title: t.term,
    excerpt: t.definition.slice(0, 120) + (t.definition.length > 120 ? "…" : ""),
    Icon: t.category === "cepage" ? Grape : t.category === "terroir" ? Layers : t.category === "vini" ? Wine : Book,
    tags: [cat?.label || "", `wset${t.wset}`],
    category: t.category,
    wset: t.wset,
  };
});

// Combined index used by the CommandBar's local search.
export const STATIC_INDEX = [...STATIC_PAGES, ...GLOSSARY_INDEX];

// Simple substring + word-boundary scoring. Returns items scored > 0.
export function searchStaticIndex(query, limit = 8) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return [];
  const nq = q.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const scored = [];
  for (const it of STATIC_INDEX) {
    const title = (it.title || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const excerpt = (it.excerpt || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const tags = (it.tags || []).join(" ").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    let score = 0;
    if (title === nq) score += 100;
    else if (title.startsWith(nq)) score += 50;
    else if (title.includes(nq)) score += 30;
    if (tags.includes(nq)) score += 15;
    if (excerpt.includes(nq)) score += 8;
    // Boost pillar/page entries so they float above single glossary terms.
    if (it.kind === "pillar" || it.kind === "page") score += 5;
    if (score > 0) scored.push([score, it]);
  }
  scored.sort((a, b) => b[0] - a[0]);
  return scored.slice(0, limit).map(([, it]) => it);
}
